﻿import { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { Card, Button, Radio, DatePicker, Descriptions, Alert } from "antd";
import locale from "antd/es/date-picker/locale/fr_FR";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import "./Styles.css";
import AllPatientsCalendarCard from "./AllPatientsCalendarCard";

dayjs.extend(quarterOfYear);
const { RangePicker } = DatePicker;

function mean(values) {
  // Moyenne d'un tableau numerique
  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

const ticksFromDomain = (domain, tickCount = 7) => {
  const [rawMin, rawMax] = Array.isArray(domain) ? domain : [0, 1];
  const min = Number(rawMin);
  const max = Number(rawMax);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  if (min === max) return Array.from(new Set([min, 0])).sort((a, b) => a - b);
  const safeCount = Math.max(2, tickCount);
  const step = (max - min) / (safeCount - 1);
  const ticks = [];
  for (let i = 0; i < safeCount; i += 1) {
    ticks.push(Number((min + step * i).toFixed(2)));
  }
  if (!ticks.some((t) => Math.abs(t) < 0.000001)) ticks.push(0);
  return Array.from(new Set(ticks)).sort((a, b) => a - b);
};

export default function AllPatientsProgramDashboard({
  sessions = [],
  isLoadingSessions = false,
}) {
  // Sessions globales (tous patients) pour calculer moyennes + courbes
  const safeSessions = useMemo(
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions]
  );

  const [averageViewMode, setAverageViewMode] = useState("month");
  const [currentPeriodDate, setCurrentPeriodDate] = useState(dayjs());
  const [dateRange, setDateRange] = useState([null, null]);

  const getCurrentPeriodRange = useCallback(() => {
    const d = currentPeriodDate;
    switch (averageViewMode) {
      case "week":
        return [dayjs(d).startOf("week"), dayjs(d).endOf("week")];
      case "quarter":
        return [dayjs(d).startOf("quarter"), dayjs(d).endOf("quarter")];
      case "year":
        return [dayjs(d).startOf("year"), dayjs(d).endOf("year")];
      case "month":
      default:
        return [dayjs(d).startOf("month"), dayjs(d).endOf("month")];
    }
  }, [averageViewMode, currentPeriodDate]);

  useEffect(() => {
    setDateRange(getCurrentPeriodRange());
  }, [getCurrentPeriodRange]);

  const filteredSessions = useMemo(() => {
    const [start, end] = dateRange;
    if (!start || !end) return safeSessions;
    const startD = dayjs(start).startOf("day");
    const endD = dayjs(end).endOf("day");
    return safeSessions.filter((s) => {
      if (!s?.date) return false;
      const d = dayjs(s.date);
      return !d.isBefore(startD) && !d.isAfter(endD);
    });
  }, [safeSessions, dateRange]);

  const summary = useMemo(() => {
    // Resume des indicateurs moyens sur la plage de dates
    const walking = filteredSessions
      .map((s) => Number(s?.walkingTime))
      .filter((n) => Number.isFinite(n));
    const difficulty = filteredSessions
      .map((s) => Number(s?.difficultyLevel))
      .filter((n) => Number.isFinite(n));
    const pain = filteredSessions
      .map((s) => Number(s?.painLevel))
      .filter((n) => Number.isFinite(n));
    const exercises = filteredSessions
      .map((s) => Number(s?.accomplishedExercice))
      .filter((n) => Number.isFinite(n));

    return {
      count: filteredSessions.length,
      walking: mean(walking),
      difficulty: mean(difficulty),
      pain: mean(pain),
      exercises: mean(exercises),
    };
  }, [filteredSessions]);

  const progressionData = useMemo(() => {
    const byDate = new Map();

    filteredSessions.forEach((s) => {
      const date = dayjs(s.date).format("YYYY-MM-DD");
      if (!byDate.has(date)) {
        byDate.set(date, {
          date,
          walking: [],
          difficulty: [],
          pain: [],
          exercises: [],
        });
      }
      const bucket = byDate.get(date);
      const walk = Number(s?.walkingTime);
      const diff = Number(s?.difficultyLevel);
      const p = Number(s?.painLevel);
      const ex = Number(s?.accomplishedExercice);
      if (Number.isFinite(walk)) bucket.walking.push(walk);
      if (Number.isFinite(diff)) bucket.difficulty.push(diff);
      if (Number.isFinite(p)) bucket.pain.push(p);
      if (Number.isFinite(ex)) bucket.exercises.push(ex);
    });

    return Array.from(byDate.values())
      .map((b) => ({
        date: b.date,
        walking: mean(b.walking),
        difficulty: mean(b.difficulty),
        pain: mean(b.pain),
        exercises: mean(b.exercises),
      }))
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  }, [filteredSessions]);

  const progressionDomain = useMemo(() => {
    if (!progressionData.length) return [0, 1];
    const values = progressionData
      .flatMap((row) => [row.difficulty, row.pain, row.walking, row.exercises])
      .map(Number)
      .filter((n) => Number.isFinite(n));
    if (!values.length) return [0, 1];
    const min = Math.min(0, Math.floor(Math.min(...values)));
    const max = Math.max(1, Math.ceil(Math.max(...values)), 0);
    return [min, max];
  }, [progressionData]);

  const getPeriodLabel = useCallback(() => {
    const date = currentPeriodDate;
    switch (averageViewMode) {
      case "week":
        return `Semaine du ${date.startOf("week").format("DD/MM/YYYY")}`;
      case "quarter":
        return `${date.quarter()}e trimestre ${date.year()}`;
      case "year":
        return date.format("YYYY");
      case "month":
      default:
        return date.format("MMMM YYYY");
    }
  }, [averageViewMode, currentPeriodDate]);

  const changePeriod = (direction) => {
    setCurrentPeriodDate((prev) => {
      if (direction === "previous") {
        if (averageViewMode === "week") return dayjs(prev).subtract(1, "week");
        if (averageViewMode === "quarter") return dayjs(prev).subtract(1, "quarter");
        if (averageViewMode === "year") return dayjs(prev).subtract(1, "year");
        return dayjs(prev).subtract(1, "month");
      }
      if (averageViewMode === "week") return dayjs(prev).add(1, "week");
      if (averageViewMode === "quarter") return dayjs(prev).add(1, "quarter");
      if (averageViewMode === "year") return dayjs(prev).add(1, "year");
      return dayjs(prev).add(1, "month");
    });
  };

  return (
    <div>
      <h2 className="section-header">Moyenne des sessions (tous patients)</h2>
      <Card>
        <div className="paired-top-section">
          <div className="average-controls-container">
            <div className="average-view-selector">
              <Radio.Group
                value={averageViewMode}
                onChange={(e) => setAverageViewMode(e.target.value)}
              >
                <Radio.Button value="week">Semaine</Radio.Button>
                <Radio.Button value="month">Mois</Radio.Button>
                <Radio.Button value="quarter">Trimestre</Radio.Button>
                <Radio.Button value="year">Annee</Radio.Button>
              </Radio.Group>
            </div>

            <div className="average-date-selector">
              <label>Filtrer les moyennes par date :</label>
              <RangePicker
                onChange={(dates) => setDateRange(dates || [null, null])}
                locale={locale}
                allowClear
                format="DD/MM/YYYY"
                value={dateRange}
              />
            </div>

            <div className="average-period-navigation">
              <Button onClick={() => changePeriod("previous")}>Periode precedente</Button>
              <span className="period-indicator">{getPeriodLabel()}</span>
              <Button onClick={() => changePeriod("next")}>Periode suivante</Button>
            </div>
          </div>
          {summary.count ? (
            <>
              <Descriptions bordered size="small" column={1} title="Moyennes globales">
                <Descriptions.Item label="Nb Sessions">{summary.count}</Descriptions.Item>
                <Descriptions.Item label="Difficulte Moy.">
                  {summary.difficulty == null ? "N/A" : summary.difficulty.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Douleur Moy.">
                  {summary.pain == null ? "N/A" : summary.pain.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Marche Moy. (min)">
                  {summary.walking == null ? "N/A" : summary.walking.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Exercices Moy.">
                  {summary.exercises == null ? "N/A" : summary.exercises.toFixed(2)}
                </Descriptions.Item>
              </Descriptions>

              <div className="average-chart-full-width">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={progressionData} margin={{ top: 8, right: 20, bottom: 30, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(tick) => dayjs(tick).format("DD/MM")}
                      angle={-45}
                      textAnchor="end"
                      height={55}
                    />
                    <YAxis
                      domain={progressionDomain}
                      ticks={ticksFromDomain(progressionDomain)}
                      interval={0}
                    />
                    <ReferenceLine y={0} stroke="#7f8c8d" strokeDasharray="4 4" />
                    <Tooltip labelFormatter={(label) => dayjs(label).format("DD MMM YYYY")} />
                    <Legend />
                    <Line type="monotone" dataKey="difficulty" stroke="#8884d8" name="Difficulte" dot={false} />
                    <Line type="monotone" dataKey="pain" stroke="#82ca9d" name="Douleur" dot={false} />
                    <Line type="monotone" dataKey="walking" stroke="#0088FE" name="Temps marche" dot={false} />
                    <Line type="monotone" dataKey="exercises" stroke="#ff7300" name="Exercices" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <Alert type="info" showIcon message="Aucune session dans la plage de dates selectionnee." />
          )}
        </div>

        <div className="dashboard-subsection">
          <h3 className="dashboard-subsection-title">Calendrier des sessions</h3>
          <AllPatientsCalendarCard
            sessions={safeSessions}
            isLoading={isLoadingSessions}
            embedded
          />
        </div>
      </Card>
    </div>
  );
}

AllPatientsProgramDashboard.propTypes = {
  sessions: PropTypes.array,
  isLoadingSessions: PropTypes.bool,
};



